import { v4 as uuid } from 'uuid';
import { ApplicationStatus, ApplicationType } from './collections';
import type {
	ApplicationStub,
	ApplicationTemplate,
	ApplicationTemplateShort,
	AttachmentRequest,
	AttachmentSlot,
	AttachmentsForm,
	AttachmentsFormShort,
	AwardForm,
	ContributionsForm,
	EducationForm,
	ExpensesForm,
	FamilyForm,
	IncomesForm,
	ProfileForm,
	ProjectionsForm,
	ScoutingForm,
} from '../../types/forms';
import type { Applicant } from '../../types/domain';

export function lettersOnly(value: string): boolean {
	return /^[a-zA-Z]*$/.test(value);
}

export function lettersAndSpacesOnly(value: string): boolean {
	return /^[a-zA-Z\s]*$/.test(value);
}

export function numbersOnly(value: string): boolean {
	return /^\d*$/.test(value);
}

export function emailsOnly(value: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function decimalsOnly(value: string): boolean {
	return /^\d*\.?\d*$/.test(value);
}

export function locationOnly(value: string): boolean {
	return /^[a-zA-Z]+(?:-[a-zA-Z]+)?,\s*[a-zA-Z]+(?:-[a-zA-Z]+)?$/.test(value);
}

export function notUndefined(value: unknown): boolean {
	return value !== 'undefined' && value !== undefined && value !== null && value !== '';
}

export const blankProfile: ProfileForm = {
	applicantID: uuid(),
	applicantFirstName: '',
	applicantMiddleInitial: '',
	applicantLastName: '',
	applicantDOB: null,
	applicantMailingAddress: null,
	applicantHomePhone: '',
	applicantCellPhone: '',
	applicantEmailAddress: '',
	completedBy: '',
};

export const blankFamily: FamilyForm = {
	familyID: uuid(),
	familyMembers: [],
	completedBy: '',
};

export const blankEducation: EducationForm = {
	educationRecordID: uuid(),
	schoolName: '',
	expectedGraduationDate: null,
	major: '',
	currentGPA: '',
	previousSchools: [],
	completedBy: '',
};

export const blankScouting: ScoutingForm = {
	scoutingRecordID: uuid(),
	scoutingExperiences: [],
	collegeReservesFlag: false,
	currentUnit: 'undefined',
	completedBy: '',
};

export const blankExpenses: ExpensesForm = {
	expensesID: uuid(),
	tuitionCost: '',
	roomAndBoardCost: '',
	bookCost: '',
	commutingCost: '',
	otherExpenses: [],
	completedBy: '',
};

export const blankIncomes: IncomesForm = {
	incomesReportID: uuid(),
	summerEarnings: '',
	fallEarnings: '',
	winterEarnings: '',
	springEarnings: '',
	earningsAppliedToEducation: '',
	savingsAppliedToEducation: '',
	collegeAward: '',
	loansAmount: '',
	otherIncomeSources: [],
	completedBy: '',
};

export const blankContributions: ContributionsForm = {
	contributionsID: uuid(),
	p1ExpectedAnnualIncome: '',
	p2ExpectedAnnualIncome: '',
	parentInvestmentIncome: '',
	parentsOwnOrRentHome: '',
	parentsMaritalStatus: '',
	siblingSchools: [],
	anyExtraordinaryExpenses: '',
	completedBy: '',
};

export const blankProjections: ProjectionsForm = {
	projectionsID: uuid(),
	applicantEarnings: '',
	applicantSavings: '',
	applicantFamily: '',
	request: '',
	completedBy: '',
};

export const blankAttachment: AttachmentSlot = {
	displayName: '',
	home: '',
	refLoc: '',
	uploadedBy: '',
	requestID: '',
};

export const blankAttachments: AttachmentsForm = {
	attachmentsID: uuid(),
	applicantPersonalLetter: blankAttachment,
	academicRecommendationLetter: blankAttachment,
	religiousRecommendationLetter: blankAttachment,
	scoutRecommendationLetter: blankAttachment,
	studentAidReport: blankAttachment,
	academicTranscript: blankAttachment,
	acceptanceLetter: blankAttachment,
	completedBy: '',
};

export const blankAward: AwardForm = {
	awardID: uuid(),
	awardAmount: '',
	applicantID: '',
	applicationID: '',
	completedBy: '',
	createdOn: '',
	deadline: '',
	message: '',
	type: '',
};

export const blankAttachments2: AttachmentsFormShort = {
	attachmentsID: uuid(),
	applicantPersonalLetter: blankAttachment,
	academicTranscript: blankAttachment,
	completedBy: '',
};

export const templateApp: ApplicationTemplate = {
	id: uuid(),
	profile: blankProfile,
	family: blankFamily,
	education: blankEducation,
	scouting: blankScouting,
	expenses: blankExpenses,
	incomes: blankIncomes,
	contributions: blankContributions,
	projections: blankProjections,
	attachments: blankAttachments,
	awards: [blankAward],
	completedBy: '',
	type: '',
	status: ApplicationStatus.started,
	submittedOn: new Date().toLocaleString(),
	window: '5/15/2024, 11:59:59 PM',
};

export const templateApp2: ApplicationTemplateShort = {
	id: uuid(),
	profile: blankProfile,
	education: blankEducation,
	attachments: blankAttachments2,
	awards: [blankAward],
	completedBy: '',
	type: ApplicationType.scholarship,
	status: ApplicationStatus.started,
	submittedOn: new Date().toLocaleString(),
	window: '5/15/2024, 11:59:59 PM',
};

export const blankApp: ApplicationStub = {
	id: uuid(),
	profile: uuid(),
	family: uuid(),
	education: uuid(),
	scouting: uuid(),
	expenses: uuid(),
	incomes: uuid(),
	contributions: uuid(),
	projections: uuid(),
	attachments: uuid(),
	awards: [uuid()],
	completedBy: uuid(),
	type: '',
	status: '',
	submittedOn: new Date().toLocaleString(),
	window: '5/15/2024, 11:59:59 PM',
};

export const blankApp2: ApplicationStub = {
	id: uuid(),
	profile: uuid(),
	education: uuid(),
	attachments: uuid(),
	awards: [uuid()],
	completedBy: uuid(),
	type: '',
	status: '',
	submittedOn: new Date().toLocaleString(),
	window: '5/15/2024, 11:59:59 PM',
};

export const blankRequest: AttachmentRequest = {
	applicationID: uuid(),
	attachmentType: '',
	attachmentsID: uuid(),
	attempts: 0,
	completed: false,
	email: '',
	expiryDate: '',
	fromName: '',
	id: uuid(),
	name: '',
	pinCode: '',
	relation: '',
};

export const blankApplicant: Applicant = {
	id: uuid(),
	firstName: '',
	lastName: '',
	callMe: '',
	picture: {},
	school: '',
	major: '',
	gradYear: '',
	organization: '',
	applications: [],
	email: '',
	cell: '',
	homePhone: '',
	auth: '',
};
